import {
  type ChangeEventHandler,
  type FocusEventHandler,
  type JSX,
  type ReactNode,
} from "react";
import { Eye_1Icon, EyeDisableIcon } from "vega-icons";

import { FormLabel } from "@vega-core/components/Form/Layout/FormLabel";
import { ValidationErrorText } from "@vega-core/components/Form/Layout/ValidationError";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

type PasswordInputProps = {
  autocomplete?: "current-password" | "new-password";
  background?: string;
  error?: string;
  hint?: string;
  icon?: JSX.Element;
  label?: string;
  labelTag?: ReactNode;
  name: string;
  onBlur: FocusEventHandler;
  onChange: ChangeEventHandler;
  placeholder?: string;
  type?: string;
  value: string;
  withBorder?: boolean;
};

export const PasswordInput = ({
  autocomplete = "current-password",
  background,
  error,
  hint,
  icon,
  label,
  labelTag,
  name,
  onBlur,
  onChange,
  placeholder,
  value,
  withBorder,
}: PasswordInputProps) => {
  const { value: passwordShown, toggle: togglePassword } = useBoolean(false);

  return (
    <div>
      {isTruthy(label) ? (
        <FormLabel forName={name} hint={hint} label={label} tag={labelTag} />
      ) : null}
      <div
        className={cnTwMerge(
          "flex flex-row rounded-companyBorderRadius border border-transparent transition focus-within:border-brand-default hover:border-brand-default",
          {
            "!border-border/negative": isTruthy(error),
            "border-border/primary": isTruthy(withBorder),
          },
        )}
      >
        {icon ? (
          <div
            className={cnTwMerge(
              "items-senter flex items-center rounded-l-companyBorderRadius pl-2",
              {
                "bg-dark-gray": isFalsy(background),
              },
              background,
            )}
          >
            {icon}
          </div>
        ) : null}
        <input
          aria-label={name}
          autoComplete={autocomplete}
          className={cnTwMerge(
            "form-control block w-full border-0 px-2 py-3.5 text-sm placeholder-text/disabled focus:border-transparent focus:ring-0 focus:ring-offset-0",
            {
              "!bg-dark-gray": isFalsy(background),
              "rounded-l-companyBorderRadius": !icon,
            },
            background,
          )}
          data-testid={`${name}-password-field`}
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          placeholder={placeholder}
          type={passwordShown ? "text" : "password"}
          value={value}
        />
        <button
          className={cnTwMerge(
            "m-0 rounded-r-companyBorderRadius border-0 bg-clip-padding px-2 font-normal text-neutral-700 transition ease-in-out",
            background,
          )}
          data-testid={`${name}-password-toggle`}
          onClick={togglePassword}
          type="button"
        >
          {passwordShown ? (
            <Eye_1Icon className="block w-4 h-4 fill-text/primary" />
          ) : (
            <EyeDisableIcon className="block w-4 h-4 fill-text/primary" />
          )}
        </button>
      </div>
      {isTruthy(error) ? (
        <ValidationErrorText dataTestId={error} withMargin>
          {error}
        </ValidationErrorText>
      ) : null}
    </div>
  );
};
